export interface NotificationsGql {
	items: NotificationItem[];
	__typename: string;
}

export enum NotificationStatus {
	ACTIVE = 'ACTIVE',
	VIEWED = 'VIEWED',
	ARCHIVED = 'ARCHIVED',
	DISMISSED = 'DISMISSED',
	DELETED = 'DELETED',
}

export enum SeverityLevel {
	INFO = 'INFO',
	ERROR = 'ERROR',
	WARNING = 'WARNING',
	SUCCESS = 'SUCCESS',
}

export type NotificationConfig = {
	applicationId: string; // 'info360' | 'info360-asset'
	tenantId: string;
	userId: string;
	product: string;
	notificationDisabled?: boolean;
};

export type NotificationHubState = {
	config: NotificationConfig;
	notificationItems: NotificationItem[];
	notificationManagement: {
		rowsPerPage: number;
		currentPage: number;
		timeStampOrder: 'asc' | 'desc';
		deleteIds: string[];
		searchText: string | undefined;
		typeFilter: string;
		startDate: string | null;
		endDate: string | null;
	};
};

export type CreateNotificationInput = {
	product: string;
	tenantId?: string | null;
	userId?: string | null;
	channels: string;
	createdAt: number;
	eventId?: string | null;
	metaData?: string | null;
	notificationText: string;
	notificationType: string;
	senderUserId?: string | null;
	timestamp: number;
	severityLevel?: string | null;
	source?: string | null;
	expirationTimeStamp?: number | null;
	status?: NotificationStatus | null;
};

export type NotificationItem = {
	__typename: 'Notification';
	productId_tenantId_userId: string;
	id: string;
	product: string;
	productId_tenantId: string;
	tenantId: string;
	userId: string;
	timestamp: number;
	createdAt: number;
	receivedAt: number;
	status: NotificationStatus;
	metaData: string;
	channels: string;
	notificationType: string;
	eventId: string;
	expirationTimeStamp?: number;
	notificationText: string;
	senderUserId: string;
	severityLevel: SeverityLevel;
	source: string;
};

export type MultipleStatusUpdateParams = {
	product: string;
	tenantId?: string | null;
	userId?: string | null;
	ids: Array<string | null>;
	status: NotificationStatus;
};

export type SendNotification = {
	__typename: 'SendNotification';
	userId?: string | null;
	tenantId?: string | null;
	product: string;
	notificationBody: string;
};

export type ChangeNotificationsStatusParams = {
	product: string;
	tenantId?: string | null;
	userId?: string | null;
	ids: Array<string | null>;
};

export type ToggleNotificationParams = {
	applicationId?: string | null;
	tenantId?: string | null;
	userId?: string | null;
	productId?: string | null;
	notificationType?: string | null;
	priority?: string | null;
	notificationDisabled: boolean;
};

export type NotificationSettings = {
	__typename: 'NotificationSettings';
	pk: string;
	id: string;
	applicationId?: string | null;
	tenantId?: string | null;
	userId?: string | null;
	notificationDisabled?: boolean | null;
	productId?: string | null;
	notificationType?: string | null;
	updatedAt?: number | null;
	updatedByUserId?: string | null;
	createdAt?: number | null;
	createdByUserId?: string | null;
};

export type ListNotificationsResponse = {
	__typename: 'ListNotificationsResponse';
	items?: Array<Notification | null> | null;
	nextToken?: string | null;
};

export type GetNotificationSettingsParams = {
	applicationId?: string | null;
	tenantId?: string | null;
	userId?: string | null;
	productId?: string | null;
	notificationType?: string | null;
	priority?: string | null;
};

export type CreateNotificationMutationVariables = {
	input: CreateNotificationInput;
};

export type CreateNotificationMutation = {
	CreateNotification: {
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	};
};

export type UpdateNotificationStatusMutationVariables = {
	product: string;
	tenantId?: string | null;
	userId?: string | null;
	id: string;
	status: NotificationStatus;
};

export type UpdateNotificationStatusMutation = {
	UpdateNotificationStatus: {
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	};
};

export type MultipleStatusUpdateMutationVariables = {
	params?: MultipleStatusUpdateParams | null;
};

export type MultipleStatusUpdateMutation = {
	MultipleStatusUpdate?: Array<{
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	} | null> | null;
};

export type SendNotificationMutationVariables = {
	userId?: string | null;
	tenantId?: string | null;
	product: string;
	notificationBody: string;
};

export type SendNotificationMutation = {
	SendNotification?: {
		__typename: 'SendNotification';
		userId?: string | null;
		tenantId?: string | null;
		product: string;
		notificationBody: string;
	} | null;
};

export type ArchiveNotificationsMutationVariables = {
	params?: ChangeNotificationsStatusParams | null;
};

export type ArchiveNotificationsMutation = {
	ArchiveNotifications?: Array<{
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	} | null> | null;
};

export type DismissNotificationsMutationVariables = {
	params?: ChangeNotificationsStatusParams | null;
};

export type DismissNotificationsMutation = {
	DismissNotifications?: Array<{
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	} | null> | null;
};

export type DeleteNotificationsMutationVariables = {
	params?: ChangeNotificationsStatusParams | null;
};

export type DeleteNotificationsMutation = {
	DeleteNotifications?: Array<{
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	} | null> | null;
};

export type MarkNotificationsAsViewedMutationVariables = {
	params?: ChangeNotificationsStatusParams | null;
};

export type MarkNotificationsAsViewedMutation = {
	MarkNotificationsAsViewed?: Array<{
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	} | null> | null;
};

export type ToggleNotificationsMutationVariables = {
	params?: ToggleNotificationParams | null;
};

export type ToggleNotificationsMutation = {
	ToggleNotifications?: {
		__typename: 'NotificationSettings';
		pk: string;
		id: string;
		applicationId?: string | null;
		tenantId?: string | null;
		userId?: string | null;
		notificationDisabled?: boolean | null;
		productId?: string | null;
		notificationType?: string | null;
		updatedAt?: number | null;
		updatedByUserId?: string | null;
		createdAt?: number | null;
		createdByUserId?: string | null;
	} | null;
};

export type QueryNotificationsQueryVariables = {
	userId?: string | null;
	tenantId?: string | null;
	product: string;
	status?: Array<NotificationStatus | null> | null;
	withUserId?: boolean | null;
	limit?: number | null;
	nextToken?: string | null;
};

export type QueryNotificationsQuery = {
	QueryNotifications?: {
		__typename: 'ListNotificationsResponse';
		items?: Array<{
			__typename: 'Notification';
			productId_tenantId_userId: string;
			id: string;
			product: string;
			productId_tenantId: string;
			tenantId: string;
			userId: string;
			timestamp?: number | null;
			createdAt?: number | null;
			receivedAt?: number | null;
			status?: NotificationStatus | null;
			metaData?: string | null;
			channels?: string | null;
			notificationType?: string | null;
			eventId?: string | null;
			expirationTimeStamp?: number | null;
			notificationText?: string | null;
			senderUserId?: string | null;
			severityLevel?: string | null;
			source?: string | null;
		} | null> | null;
		nextToken?: string | null;
	} | null;
};

export type ScanNotificationsQueryVariables = {
	product: string;
	userId?: string | null;
	tenantId?: string | null;
	limit?: number | null;
	nextToken?: string | null;
};

export type ScanNotificationsQuery = {
	ScanNotifications?: {
		__typename: 'ListNotificationsResponse';
		items?: Array<{
			__typename: 'Notification';
			productId_tenantId_userId: string;
			id: string;
			product: string;
			productId_tenantId: string;
			tenantId: string;
			userId: string;
			timestamp?: number | null;
			createdAt?: number | null;
			receivedAt?: number | null;
			status?: NotificationStatus | null;
			metaData?: string | null;
			channels?: string | null;
			notificationType?: string | null;
			eventId?: string | null;
			expirationTimeStamp?: number | null;
			notificationText?: string | null;
			senderUserId?: string | null;
			severityLevel?: string | null;
			source?: string | null;
		} | null> | null;
		nextToken?: string | null;
	} | null;
};

export type GetNotificationSettingQueryVariables = {
	params?: GetNotificationSettingsParams | null;
};

export type GetNotificationSettingQuery = {
	GetNotificationSetting?: {
		__typename: 'NotificationSettings';
		pk: string;
		id: string;
		applicationId?: string | null;
		tenantId?: string | null;
		userId?: string | null;
		notificationDisabled?: boolean | null;
		productId?: string | null;
		notificationType?: string | null;
		updatedAt?: number | null;
		updatedByUserId?: string | null;
		createdAt?: number | null;
		createdByUserId?: string | null;
	} | null;
};

export type SubscribeToNewNotificationsSubscriptionVariables = {
	userId?: string | null;
	tenantId?: string | null;
	product: string;
};

export type SubscribeToNewNotificationsSubscription = {
	subscribeToNewNotifications?: {
		__typename: 'SendNotification';
		userId?: string | null;
		tenantId?: string | null;
		product: string;
		notificationBody: string;
	} | null;
};

export type AddedNotificationSubscriptionVariables = {
	userId?: string | null;
	tenantId?: string | null;
	product?: string | null;
};

export type AddedNotificationSubscription = {
	addedNotification?: {
		__typename: 'Notification';
		productId_tenantId_userId: string;
		id: string;
		product: string;
		productId_tenantId: string;
		tenantId: string;
		userId: string;
		timestamp?: number | null;
		createdAt?: number | null;
		receivedAt?: number | null;
		status?: NotificationStatus | null;
		metaData?: string | null;
		channels?: string | null;
		notificationType?: string | null;
		eventId?: string | null;
		expirationTimeStamp?: number | null;
		notificationText?: string | null;
		senderUserId?: string | null;
		severityLevel?: string | null;
		source?: string | null;
	} | null;
};
